import React, { useCallback, useEffect, useRef, useState } from "react";

const MINUTES = [0, 15, 30, 45];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function snapMinute(m) {
  if (Number.isNaN(m)) return 0;
  return MINUTES.reduce((prev, curr) =>
    Math.abs(curr - m) < Math.abs(prev - m) ? curr : prev,
  );
}

function parseHHMM(s) {
  if (!s || typeof s !== "string") return { h: 12, m: 0 };
  const parts = s.split(":");
  const h = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0));
  const mRaw = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0));
  return { h, m: snapMinute(mRaw) };
}

function formatHHMM(h, m) {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatDisplay(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m) || hhmm.indexOf(":") === -1)
    return null;
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Déclencheur identique aux autres champs `.field-co input` ; popover pour choisir l’heure.
 */
export default function CheckoutTimePicker({
  id = "checkoutLatestTime",
  value,
  onChange,
  hasError,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [draft, setDraft] = useState(() => parseHHMM(value));

  useEffect(() => {
    if (open) setDraft(parseHHMM(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commit = useCallback(() => {
    onChange(formatHHMM(draft.h, draft.m));
    setOpen(false);
  }, [draft, onChange]);

  const display = formatDisplay(value);

  return (
    <div className="co-time-picker-root" ref={wrapRef}>
      <div
        id={id}
        role="button"
        tabIndex={0}
        className={`co-time-pill${open ? " is-open" : ""}${hasError ? " input-error" : ""}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
          if (e.key === "Escape") setOpen(false);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="co-time-pill-value">
          {display ? (
            display
          ) : (
            <span className="co-time-pill-placeholder">Choose a time</span>
          )}
        </span>
      </div>

      {open ? (
        <div
          className="co-time-cal-wrap"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${id}-title`}
        >
          <div className="co-time-cal-box">
            <div className="co-time-cal-head">
              <span className="co-time-cal-title" id={`${id}-title`}>
                Latest delivery time
              </span>
              <button
                type="button"
                className="co-time-cal-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="co-time-cal-section">
              <div className="co-time-cal-slabel">Hour</div>
              <div className="co-time-h-grid">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`co-time-cell${draft.h === h ? " is-sel" : ""}`}
                    onClick={() => setDraft((d) => ({ ...d, h }))}
                  >
                    {String(h).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>

            <div className="co-time-cal-section">
              <div className="co-time-cal-slabel">Minute</div>
              <div className="co-time-m-row">
                {MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`co-time-cell co-time-m${draft.m === m ? " is-sel" : ""}`}
                    onClick={() => setDraft((d) => ({ ...d, m }))}
                  >
                    {String(m).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" className="co-time-cal-done" onClick={commit}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
